import * as React from 'react';
import { cn } from '@/lib/utils';

export interface DottedLineSeparatorProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** セパレーターの幅（pxまたは%） */
  width?: number | string;
  /** セパレーターの色 */
  color?: string;
  /** 線の太さ（px） */
  strokeWidth?: number;
}

/**
 * DottedLineSeparator - ドット線セパレーター（SVG版）
 *
 * Figmaのドット線デザインを再現したSVGベースのセパレーターです。
 * 主にリスト項目の下に配置する装飾線として使用します。
 *
 * @example
 * ```tsx
 * // デフォルト
 * <DottedLineSeparator />
 *
 * // 幅指定
 * <DottedLineSeparator width={300} />
 * <DottedLineSeparator width="100%" />
 *
 * // 色指定
 * <DottedLineSeparator color="#C3C3C0" />
 *
 * // 太さ指定
 * <DottedLineSeparator strokeWidth={1.2} />
 * ```
 */
export const DottedLineSeparator = React.forwardRef<
  HTMLDivElement,
  DottedLineSeparatorProps
>(
  (
    {
      className,
      width = '100%',
      color = '#C3C3C0',
      strokeWidth = 1.2,
      style,
      ...props
    },
    ref,
  ) => {
    const widthValue = typeof width === 'number' ? `${width}px` : width;

    return (
      <div
        ref={ref}
        className={cn('inline-block', className)}
        style={{
          width: widthValue,
          height: `${strokeWidth + 0.5}px`,
          ...style,
        }}
        {...props}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 302 1.2"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          style={{ display: 'block' }}
        >
          <line
            x1="0.6"
            y1="0.6"
            x2="301.4"
            y2="0.6"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray="0.1 3.6"
          />
        </svg>
      </div>
    );
  },
);

DottedLineSeparator.displayName = 'DottedLineSeparator';
